import { API_CONFIG } from '../config/api.config';
import type { WebSocketMessage, ClientMessage, SubscriptionPayload, NotificationPayload } from '../types/notifications';

type MessageHandler = (message: WebSocketMessage) => void;
type NotificationHandler = (notification: NotificationPayload) => void;
type ConnectionHandler = (connected: boolean) => void;

export class NotificationWebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private messageHandlers = new Set<MessageHandler>();
  private notificationHandlers = new Set<NotificationHandler>();
  private connectionHandlers = new Set<ConnectionHandler>();
  private connectionId: string | null = null;
  private token: string | null = null;
  private generation = 0;
  private currentSubscription: SubscriptionPayload | null = null;

  connect(token: string): void {
    if (this.token === token && this.ws && this.ws.readyState < WebSocket.CLOSING) return;
    this.disconnect();
    this.token = token;
    this.open();
  }

  private open(): void {
    if (!this.token) return;
    const generation = this.generation;
    const url = `${API_CONFIG.BASE_URL.replace(/\/$/, '').replace(/^http/, 'ws')}/notifications/ws?token=${encodeURIComponent(this.token)}`;
    try {
      const ws = new WebSocket(url);
      this.ws = ws;
      const current = () => generation === this.generation && this.ws === ws;
      ws.onopen = () => {
        if (!current()) return;
        this.reconnectAttempts = 0;
        this.notifyConnectionHandlers(true);
        this.stopHeartbeat();
        this.pingInterval = setInterval(() => this.send({ type: 'ping' }), 25000);
        if (this.currentSubscription) this.subscribe(this.currentSubscription);
      };
      ws.onmessage = event => {
        if (!current()) return;
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          if (message.type === 'connected') {
            this.connectionId = message.connection_id || null;
            this.notifyConnectionHandlers(true);
          } else if (message.type === 'notification' && message.payload) {
            this.notificationHandlers.forEach(handler => handler(message.payload!));
          } else if (message.type === 'ping') this.send({ type: 'pong' });
          this.messageHandlers.forEach(handler => handler(message));
        } catch { /* Ignore malformed frames; REST inbox remains available. */ }
      };
      ws.onerror = () => { /* onclose owns reconnect; avoid overlapping sockets. */ };
      ws.onclose = event => {
        if (!current()) return;
        this.ws = null;
        this.connectionId = null;
        this.stopHeartbeat();
        this.notifyConnectionHandlers(false);
        if (event.code === 4001 || event.code === 4002) return;
        this.scheduleReconnect();
      };
    } catch { this.scheduleReconnect(); }
  }

  private scheduleReconnect(): void {
    if (!this.token || this.reconnectTimer) return;
    const delay = Math.min(1000 * 2 ** Math.min(this.reconnectAttempts++, 5), 30000);
    this.reconnectTimer = setTimeout(() => { this.reconnectTimer = null; this.open(); }, delay);
  }

  private stopHeartbeat(): void {
    if (this.pingInterval) clearInterval(this.pingInterval);
    this.pingInterval = null;
  }

  disconnect(): void {
    this.generation++;
    this.token = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopHeartbeat();
    this.currentSubscription = null;
    const socket = this.ws;
    this.ws = null;
    this.connectionId = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000, 'Client disconnect');
    this.notifyConnectionHandlers(false);
  }

  send(message: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message));
  }
  subscribe(subscription: SubscriptionPayload): void {
    this.currentSubscription = subscription;
    this.send({ type: 'subscribe', payload: subscription });
  }
  unsubscribe(): void { this.currentSubscription = null; this.send({ type: 'unsubscribe' }); }
  onMessage(handler: MessageHandler): () => void { this.messageHandlers.add(handler); return () => { this.messageHandlers.delete(handler); }; }
  onNotification(handler: NotificationHandler): () => void { this.notificationHandlers.add(handler); return () => { this.notificationHandlers.delete(handler); }; }
  onConnectionChange(handler: ConnectionHandler): () => void { this.connectionHandlers.add(handler); return () => { this.connectionHandlers.delete(handler); }; }
  private notifyConnectionHandlers(connected: boolean): void { this.connectionHandlers.forEach(handler => handler(connected)); }
  isConnected(): boolean { return this.ws?.readyState === WebSocket.OPEN; }
  getConnectionId(): string | null { return this.connectionId; }
}

export const notificationWebSocket = new NotificationWebSocketService();
export default notificationWebSocket;
